/**
 * Auto-Logout Event Envelope
 *
 * Single source of the `client-portal-user-auto-logout` wire contract, shared by the
 * client emitter (`utils/track-auto-logout.utils.ts`) and the SSR emitter
 * (`utils/track-auto-logout.server.utils.ts`).
 *
 * The two origins have irreconcilable transports — the client has Pinia and a registered
 * provider, the SSR path runs in nitro middleware with neither — but they must produce
 * shape-identical documents or the Elasticsearch index is not queryable. Keeping the whole
 * envelope in one pure module is what makes that drift impossible.
 *
 * Pure by design: no transport, no config read, no `navigator` access. Callers pass the
 * user-agent string and app name in.
 */

/** Wire event name. Matches the event the mobile app already sends to the same index. */
export const AUTO_LOGOUT_EVENT_NAME = 'client-portal-user-auto-logout'

/** Distinguishes web documents from the mobile app's `gokollab-flutter-app`. */
export const AUTO_LOGOUT_EVENT_SOURCE = 'clientportal-nuxt-web'

/** Placeholder for absent identity, mirroring `BackendAnalyticsProvider.transform`. */
const NOT_AVAILABLE = 'NA'

const DEFAULT_FLAVOUR = 'clientportal'
const UNKNOWN_DEVICE = 'unknown'

/** The request whose 401 triggered the refresh. Absent for scheduled/boot/SSR refreshes. */
export interface AutoLogoutPreviousRequest {
  url: string
  statusCode: number
}

export interface AutoLogoutRefreshFailure {
  /** HTTP status of the failed refresh call. Omitted when the call never happened. */
  statusCode?: number
  /** Error message only — never a token, never a raw response body. */
  error: string
  /** Non-reversible fingerprint of the refresh token that was rejected. */
  fingerprint: string
}

/**
 * Which auto-logout site produced the event.
 *
 * - `client-boot`  — the boot plugin cleared a dead cookie before any app API call ran.
 * - `client`       — `useAuth.refreshToken()` failed mid-session and logged out.
 * - `ssr`          — the proactive pre-render refresh got a 4xx and cleared `catv2`.
 *
 * `client-boot` is separated from `client` deliberately: the boot plugin runs before location
 * init, so a boot-time death never reaches the 401 path and is likely the higher-volume case.
 * Collapsing the two would hide which one operators are actually looking at.
 */
export type AutoLogoutOrigin = 'client' | 'client-boot' | 'ssr'

export interface AutoLogoutEnvelopeInput {
  locationId: string | undefined
  contactId: string | undefined
  flavour: string
  device: string
  origin: AutoLogoutOrigin
  previousReq?: AutoLogoutPreviousRequest
  refresh: AutoLogoutRefreshFailure
}

export interface AutoLogoutTrackEventPayload {
  eventName: typeof AUTO_LOGOUT_EVENT_NAME
  locationId: string
  contactId: string
  eventData: {
    clientPortalUserId: string
    previousReq?: AutoLogoutPreviousRequest
    refreshToken: AutoLogoutRefreshFailure
    device: string
    platform: 'web'
    flavour: string
    origin: AutoLogoutOrigin
    source: typeof AUTO_LOGOUT_EVENT_SOURCE
  }
}

/** FNV-1a 32-bit offset basis and prime. */
const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193

/**
 * Stable, non-reversible fingerprint of a refresh token.
 *
 * Purpose: correlate repeated failures of the SAME rejected token across events without ever
 * transporting credential material. The mobile contract's `refreshToken.old` carries truncated
 * token bytes; any prefix or suffix of a refresh token is still credential material once it
 * reaches a broad-access Elasticsearch index, so this replaces it.
 *
 * Deliberately NOT cryptographic and never used for a security decision — it is a correlation
 * id. Synchronous on purpose: the client emit runs inside a `catch` immediately before
 * `logout()` and the SSR emit runs in nitro middleware, so neither can await
 * `crypto.subtle.digest`.
 */
export function fingerprintRefreshToken(token: string | undefined): string {
  if (!token) return 'none'

  let hash = FNV_OFFSET_BASIS
  for (let index = 0; index < token.length; index += 1) {
    hash ^= token.charCodeAt(index)
    hash = Math.imul(hash, FNV_PRIME)
  }

  return `fnv1a:${(hash >>> 0).toString(16).padStart(8, '0')}`
}

/**
 * Browser platform for the auto-logout envelope, mirroring `BaseService.getPlatformDetails`.
 *
 * Accepts `string[]` because `node:http` collapses a repeated `User-Agent` header into an array,
 * and the SSR emitter reads the header directly. Treating that array as a string made every such
 * request report `unknown`, since `Array.prototype.includes` is an exact-element match rather
 * than a substring test.
 *
 * Order matters: iPadOS reports "like Mac OS X" and Android reports "Linux", so the mobile
 * checks must run before their desktop substrings.
 */
export function resolveAutoLogoutDevice(userAgentHeader: string | string[] | undefined): string {
  const userAgent = Array.isArray(userAgentHeader) ? userAgentHeader[0] : userAgentHeader
  if (!userAgent) return UNKNOWN_DEVICE

  if (userAgent.includes('Android')) return 'android'
  if (userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod')) return 'ios'
  if (userAgent.includes('Windows')) return 'windows'
  if (userAgent.includes('Mac')) return 'mac'
  if (userAgent.includes('Linux')) return 'linux'

  return UNKNOWN_DEVICE
}

/**
 * Strips the query string and fragment from a request URL before it enters the event.
 *
 * The endpoint path is the whole diagnostic value ("which call 401'd"), while query parameters
 * can carry search terms, emails, or other caller-supplied data that must not reach a
 * broad-access analytics sink. Keeping only origin + pathname preserves the signal and drops
 * the risk.
 */
export function sanitizeAutoLogoutRequestUrl(url: string): string {
  const withoutFragment = url.split('#')[0] ?? ''
  return withoutFragment.split('?')[0] ?? ''
}

/** App flavour for the envelope, mirroring `resolveAppFlavor` in `utils/page-hit.ts`. */
export function resolveAutoLogoutFlavour(appName: unknown): string {
  const normalized = typeof appName === 'string' ? appName.trim().toLowerCase() : ''
  return normalized || DEFAULT_FLAVOUR
}

/** Trims and falls back to `NA` so a blank identity never reaches the index as an empty string. */
function orNotAvailable(value: string | undefined): string {
  return value?.trim() || NOT_AVAILABLE
}

/**
 * Builds the `/analytics/track-event` body for one refresh-failure auto-logout.
 *
 * `previousReq` and `refreshToken.statusCode` are omitted (key absent) rather than emitted as
 * empty values when the caller has nothing real to report.
 */
export function buildAutoLogoutTrackEventPayload(input: AutoLogoutEnvelopeInput): AutoLogoutTrackEventPayload {
  const contactId = orNotAvailable(input.contactId)

  return {
    eventName: AUTO_LOGOUT_EVENT_NAME,
    locationId: orNotAvailable(input.locationId),
    contactId,
    eventData: {
      clientPortalUserId: contactId,
      ...(input.previousReq ? { previousReq: input.previousReq } : {}),
      refreshToken: {
        ...(input.refresh.statusCode === undefined ? {} : { statusCode: input.refresh.statusCode }),
        error: input.refresh.error,
        fingerprint: input.refresh.fingerprint,
      },
      device: input.device,
      platform: 'web',
      flavour: input.flavour,
      origin: input.origin,
      source: AUTO_LOGOUT_EVENT_SOURCE,
    },
  }
}
